import { useNavigate } from '@change-corgi/core/react/router';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import { extractSharingResource } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { MappedSharingResource } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type {
	FacebookShareRecommendations,
	RedditShareRecommendations,
	Template,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import {
	useFlyerTemplates,
	useReadyToShare,
	useShareRecommendations,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ShareVisualImg } from 'src/app/pages/starterDashboardV2/shared/types';

import { useShareVisual } from '../../../GrowPetition/components/ShareVisual/hooks/useShareVisual';

import type { RecommendedGroupWithPlatform } from './types';

type SharingToolsData = {
	recommendedGroups: {
		isLoading: boolean;
		groups: RecommendedGroupWithPlatform[];
	};
	readyToSharePosts: {
		isLoading: boolean;
		facebookPost?: SharingPostData;
		redditPost?: SharingPostData;
		instagramStory?: SharingPostData;
	};
	shareVisual: {
		isLoading: boolean;
		image?: {
			url: string;
			variationName: string;
		};
	};
	posters: {
		isLoading: boolean;
		template?: {
			name: string;
			images: Template['images'];
		};
	};
};

type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	navigateToPosters: () => void;
	navigateToEditPetition: () => void;
};

export type UseSharingToolsReturn = ModelHookResult<SharingToolsData, SharingToolsActions>;

export type SharingPostData = {
	fullPostType: FullPostType;
	sharingResource: MappedSharingResource;
};

function buildRecommendedGroupsArray(
	reddit: RedditShareRecommendations,
	facebook: FacebookShareRecommendations,
): RecommendedGroupWithPlatform[] {
	const result: RecommendedGroupWithPlatform[] = [];

	// If there is at least one reddit group, that should be the first element
	if (reddit.length > 0) {
		result.push({ group: reddit[0], platform: 'reddit' });

		// If there are reddit and facebook group recommendations, return the first reddit group, and the first facebook group
		if (facebook.length > 0) {
			result.push({ group: facebook[0], platform: 'facebook' });
		}
		// If there's more than 1 reddit recommendation, but no facebook recommendations, return the 2 first reddit recommendations
		else if (reddit.length > 1) {
			result.push({ group: reddit[1], platform: 'reddit' });
		}
	}
	// If there are no reddit recommendations, but there are facebook recommendations, use facebook instead
	else if (facebook.length > 0) {
		result.push({ group: facebook[0], platform: 'facebook' });
		if (facebook.length > 1) {
			result.push({ group: facebook[1], platform: 'facebook' });
		}
	}

	return result;
}

function mapReadyToSharePosts(
	petition: StarterDashboardV2PageData['petition'],
	readyToSharePosts: ReturnType<typeof useReadyToShare>['data']['platformData'],
): Omit<SharingToolsData['readyToSharePosts'], 'isLoading'> {
	const facebookPost = readyToSharePosts.FACEBOOK?.items.find((post) => post.postTypeName === 'post');
	const redditPost = readyToSharePosts.REDDIT?.items.find((post) => post.postTypeName === 'post');
	const instagramStory = readyToSharePosts.INSTAGRAM?.items.find((post) => post.postTypeName === 'story');
	return {
		facebookPost: facebookPost
			? {
					fullPostType: 'facebook_post',
					sharingResource: extractSharingResource('facebook_post', {
						post: facebookPost,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
		redditPost: redditPost
			? {
					fullPostType: 'reddit_post',
					sharingResource: extractSharingResource('reddit_post', {
						post: redditPost,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
		instagramStory: instagramStory
			? {
					fullPostType: 'instagram_story',
					sharingResource: extractSharingResource('instagram_story', {
						post: instagramStory,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
	};
}

function mapShareVisualImage(shareVisualImages?: ShareVisualImg[]) {
	const bestOption = shareVisualImages?.find((image) => image.variantName === 'post_variant_c');
	if (bestOption) {
		return { url: bestOption.image.src, variationName: bestOption.variantName };
	}
	const shareVisualPostImage = shareVisualImages?.find((image) => image.variantName.startsWith('post'));
	if (shareVisualPostImage) {
		return { url: shareVisualPostImage.image.src, variationName: shareVisualPostImage.variantName };
	}
	return undefined;
}

function navigateToGroupRecommendations(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share#group-recommendations`);
}

function navigateToShareVisual(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share#social-media-content-container`);
}

function navigateToPosters(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share#share_qrcode_module`);
}

function navigateToEditPetition(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${encodeURIComponent(slug)}/edit`);
}

function navigateToAllTools(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share`);
}

function navigateToReadyToSharePosts(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share#ready-to-share-posts`);
}

export function useSharingTools(): UseSharingToolsReturn {
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const recommendedGroups = useShareRecommendations(petition.slug);
	const readyToSharePosts = useReadyToShare();

	const mappedReadyToSharePosts = mapReadyToSharePosts(petition, readyToSharePosts.data.platformData);
	const recommendedGroupsArray = buildRecommendedGroupsArray(
		recommendedGroups.data.shareRecommendations.reddit,
		recommendedGroups.data.shareRecommendations.facebook,
	);
	const shareVisual = useShareVisual();
	const flyerTemplates = useFlyerTemplates({ petition, context: 'starter_dashboard_sharing_tools' });
	const shareVisualImage = mapShareVisualImage(shareVisual.data.items);

	const template =
		flyerTemplates.find(({ name, enabled }) => name === 'flyer-b' && enabled) ??
		flyerTemplates.find(({ enabled }) => enabled) ??
		undefined;

	const navigate = useNavigate();

	return {
		data: {
			recommendedGroups: {
				isLoading: recommendedGroups.data.shareRecommendationsLoading,
				groups: recommendedGroupsArray,
			},
			readyToSharePosts: {
				isLoading: readyToSharePosts.data.isLoading,
				...mappedReadyToSharePosts,
			},
			shareVisual: {
				isLoading: shareVisual.data.shareVisualsState === 'loading',
				image: shareVisualImage,
			},
			posters: {
				isLoading: false,
				template: template ? { name: template.name, images: template.images } : undefined,
			},
		},
		actions: {
			navigateToGroupRecommendations: () => navigateToGroupRecommendations(petition.slug, navigate),
			navigateToAllTools: () => navigateToAllTools(petition.slug, navigate),
			navigateToReadyToSharePosts: () => {
				navigateToReadyToSharePosts(petition.slug, navigate);
			},
			navigateToShareVisual: () => navigateToShareVisual(petition.slug, navigate), // TODO: Implement click tracking,
			navigateToPosters: () => navigateToPosters(petition.slug, navigate), // TODO: Implement click tracking,
			navigateToEditPetition: () => navigateToEditPetition(petition.slug, navigate),
		},
	};
}
